import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate } from '../../lib/format';
import '../../styles/Print.css';

const API_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api')
  .replace(/\/api$/, '');
const fullLogoUrl = (path) => (path ? `${API_HOST}${path}` : null);

const EMPLOYMENT_LABEL = {
  full_time: 'Full-time', part_time: 'Part-time', contractor: 'Contractor',
  intern: 'Intern', consultant: 'Consultant',
};
const PERIODICITY_LABEL = {
  daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly',
  quarterly: 'Quarterly', yearly: 'Yearly',
};
const KIND_LABEL = {
  hard: 'Hard skill', soft: 'Soft skill', language: 'Language',
  tool: 'Tool', other: 'Other',
};

function pad(n) { return String(n + 1).padStart(2, '0'); }

export default function RolePrintView() {
  const { appId, roleId } = useParams();
  const api = useApi();
  const { organisation } = useWorkspace();

  const isAdminView = window.location.pathname.includes('/setup/roles/');
  const backToEditorPath = isAdminView
    ? `/apps/${appId}/setup/roles/${roleId}`
    : `/apps/${appId}/home/my-role`;

  const [tree, setTree]               = useState(null);
  const [departments, setDepartments] = useState([]);
  const [allRoles, setAllRoles]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, ds, rs] = await Promise.all([
          api.getRoleFull(appId, roleId),
          api.departments.list(appId).catch(() => ({ items: [] })),
          api.roles.list(appId).catch(() => ({ items: [] })),
        ]);
        if (cancelled) return;
        setTree(t);
        setDepartments(ds.items || []);
        setAllRoles(rs.items || []);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.error || err.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, appId, roleId]);

  if (loading) return <p className="muted" style={{ padding: 24 }}>Loading…</p>;
  if (error)   return <div className="banner banner-error" style={{ margin: 24 }}>{error}</div>;
  if (!tree)   return null;

  const dept      = departments.find((d) => d.id === tree.department_id);
  const reportsTo = allRoles.find((r) => r.id === tree.reports_to_role_id);

  const fields = [
    tree.code              && { label: 'Code',           value: tree.code },
    dept                   && { label: 'Department',      value: dept.name },
    tree.level != null     && { label: 'Level',           value: tree.level },
    tree.employment_type   && { label: 'Employment',      value: EMPLOYMENT_LABEL[tree.employment_type] || tree.employment_type },
    tree.max_yearly_ctc != null && { label: 'Max Yearly CTC', value: new Intl.NumberFormat().format(tree.max_yearly_ctc) },
    reportsTo              && { label: 'Reports To',      value: reportsTo.title },
  ].filter(Boolean);

  return (
    <div className="print-page">
      <div className="print-toolbar no-print">
        <Link className="btn-back" to={backToEditorPath}>← Back</Link>
        <button className="btn-primary" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
      </div>

      <article className="print-doc">

        {/* Header */}
        <header className="print-header">
          {(organisation?.logo_data || organisation?.logo_url) ? (
            <img
              src={organisation.logo_data || fullLogoUrl(organisation.logo_url)}
              alt={`${organisation.name} logo`}
              className="print-logo"
            />
          ) : null}
          <div className="print-org-meta">
            <h1>{organisation?.name || 'Organisation'}</h1>
            <p className="print-subtitle">Role Overview</p>
          </div>
        </header>

        {/* Role title */}
        <div className="print-title-block">
          <div className="print-role-icon">👤</div>
          <div className="print-role-title-wrap">
            <h2 className="print-role-title">{tree.title}</h2>
            {tree.code && <p className="print-role-code">{tree.code}</p>}
          </div>
        </div>

        {/* Identity fields grid */}
        {fields.length > 0 && (
          <div className="print-identity">
            {fields.map((f) => (
              <div key={f.label} className="print-identity-field">
                <div className="print-identity-label">{f.label}</div>
                <div className="print-identity-value">{f.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Purpose */}
        {tree.description ? (
          <PrintSection title="Purpose of This Role">
            <p className="print-text">{tree.description}</p>
          </PrintSection>
        ) : (
          <PrintSection title="Purpose of This Role">
            <p className="print-text" style={{ opacity: 0.4 }}>Why does this role exist? One or two sentences.</p>
          </PrintSection>
        )}

        {/* Responsibility Areas */}
        {tree.areas.length > 0 && (
          <PrintSection title="Responsibility Areas">
            <ul className="print-list">
              {tree.areas.map((area, i) => (
                <li key={area.id ?? i}>
                  <span className="print-list-num">{pad(i)}.</span>
                  <span className="print-list-body">
                    <strong>{area.name}</strong>
                    {area.description && <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>— {area.description}</span>}
                    {area.performance_parameters?.length > 0 && (
                      <ul className="print-param-bullets" style={{ marginTop: 4 }}>
                        {area.performance_parameters.map((p) => (
                          <li key={p.id}>{p.parameter}</li>
                        ))}
                      </ul>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </PrintSection>
        )}

        {/* Major Routine Activities */}
        {tree.routines?.length > 0 && (
          <PrintSection title="Major Routine Activities">
            <ul className="print-list">
              {tree.routines.map((r, i) => (
                <li key={r.id}>
                  <span className="print-list-num">{pad(i)}.</span>
                  <span className="print-list-body">
                    <strong>{r.description}</strong>
                    {r.periodicity && (
                      <span className="print-routine-period"> – {PERIODICITY_LABEL[r.periodicity] || r.periodicity}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </PrintSection>
        )}

        {/* Skills */}
        {tree.skills.length > 0 && (
          <PrintSection title="Skills Required">
            <ul className="print-list">
              {tree.skills.map((s, i) => (
                <li key={s.id}>
                  <span className="print-list-num">{pad(i)}.</span>
                  <span className="print-list-body">
                    <strong>{s.skill_name}</strong>
                    {s.skill_kind && <span className="print-tag">{KIND_LABEL[s.skill_kind] || s.skill_kind}</span>}
                    {s.required_proficiency != null && (
                      <span style={{ color: 'var(--text-secondary)', marginLeft: 8, fontSize: 12 }}>
                        Proficency: <strong style={{ color: 'var(--text-primary)' }}>{s.required_proficiency}/5</strong>
                      </span>
                    )}
                    {s.is_mandatory && <span className="print-tag" style={{ color: 'var(--brand)' }}>Mandatory</span>}
                  </span>
                </li>
              ))}
            </ul>
          </PrintSection>
        )}

        {/* Education */}
        {tree.education.length > 0 && (
          <PrintSection title="Education">
            <ul className="print-list">
              {tree.education.map((e, i) => (
                <li key={e.id}>
                  <span className="print-list-num">{pad(i)}.</span>
                  <span className="print-list-body">
                    {e.level && <strong>{e.level}</strong>}
                    {e.level && e.field && ' in '}
                    {e.field}
                    {e.min_gpa && <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}><strong style={{ color: 'var(--text-primary)' }}>{e.min_gpa}</strong> GPA</span>}
                    <span className="print-tag">{e.is_required ? 'Required' : 'Preferred'}</span>
                  </span>
                </li>
              ))}
            </ul>
          </PrintSection>
        )}

        {/* Experience */}
        {tree.experience.length > 0 ? (
          <PrintSection title="Experience">
            <ul className="print-list">
              {tree.experience.map((e, i) => (
                <li key={e.id}>
                  <span className="print-list-num">{pad(i)}.</span>
                  <span className="print-list-body">
                    {e.years_required != null && <strong>{Number(e.years_required)}+ years</strong>}
                    {e.type && <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>{e.type}</span>}
                    {e.description && <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>— {e.description}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </PrintSection>
        ) : (
          <PrintSection title="Experience">
            <p className="print-text" style={{ opacity: 0.4 }}>No experience requirements declared.</p>
          </PrintSection>
        )}

        {/* Best Performance */}
        {tree.best_performance_md && (
          <PrintSection title="Best Performance Definition">
            <p className="print-text">{tree.best_performance_md}</p>
          </PrintSection>
        )}

        {/* Footer */}
        <footer className="print-footer">
          <span>Generated from {organisation?.name || 'workspace'} · {formatDate(new Date().toISOString())}</span>
          {tree.updated_at && <span>Last updated {formatDate(tree.updated_at)}</span>}
        </footer>

      </article>
    </div>
  );
}

function PrintSection({ title, children }) {
  return (
    <section className="print-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}
